/* eslint-disable @typescript-eslint/no-explicit-any */
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useAlert } from "../../../../../../lib/alert";
import { Address } from "../../../../../../model/Address";
import {
  AddressFormElement,
  FieldDefinitionFormElement,
  FormElementType,
  HeadingFormElement,
  TaskFormExecution,
} from "../../../../types";

export const TaskSummaryFormComponent = ({
  ...props
}: {
  disabled: boolean;
  execution: TaskFormExecution;
  onGoToStep?: (stepNumber: number) => void;
}) => {
  const alert = useAlert();
  const elements = props.execution.data?.form?.elements || [];
  const validIndexes =
    props.execution.data?.wizardData?.validationIndexes || [];

  const addressAsString = (address?: Address) => {
    if (!address) return;
    return `${address.first_line}, ${address.second_line && address.second_line != "" && address.second_line + ", "}${address.city}, ${address.state} ${address.zip_code}`;
  };

  if (!elements || !validIndexes) {
    alert.error("Error loading elements for summmary.");
    return <></>;
  }

  const summaryElements: JSX.Element[] = [];
  const unableToRender: string[] = [];
  let currentStepInSummary = -1;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  elements.forEach((e, i) => {
    switch (e.type) {
      case FormElementType.Address:
        if ((e as AddressFormElement).value != undefined)
          summaryElements.push(
            <>
              <Typography sx={{ ml: 1 }}>
                {`${e.label}: ${addressAsString(e.value)}`}
              </Typography>
            </>,
          );
        return;
      case FormElementType.Heading:
        currentStepInSummary++;
        if (!(e as HeadingFormElement).hideInSummary) {
          const stepToGoTo = currentStepInSummary;
          const stepWasNotSkipped = validIndexes.includes(i);
          summaryElements.push(
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                mt: i == 0 ? 0 : 1,
                mb: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: stepWasNotSkipped ? "primary.main" : "inherit",
                  cursor: stepWasNotSkipped ? "pointer" : "default",
                  fontWeight: 700,
                  textDecoration: "underline",
                  "&:hover": stepWasNotSkipped ? { color: "primary.dark" } : {},
                  transition: "color 0.2s",
                }}
                onClick={() => {
                  if (stepWasNotSkipped) {
                    props.onGoToStep?.(stepToGoTo);
                  }
                }}
              >
                {`${e.label} ${stepWasNotSkipped ? " \u203A" : "(N/A)"}`}
              </Box>
            </Typography>,
          );
        }
        return;
      case FormElementType.NestedForm:
        return;
      case FormElementType.FieldDefinition:
        (e as FieldDefinitionFormElement).value?.forEach((field) => {
          summaryElements.push(
            <Typography sx={{ ml: 1 }}>
              {field.label}: {(field as any).value}
            </Typography>,
          );
        });
        return;
      case FormElementType.Date:
        if (!e.value?.length) return;
        if (e.value?.length && e.value.length > 1)
          summaryElements.push(
            <>
              <Typography sx={{ ml: 1 }}>{`${e.label}: `}</Typography>
              {e.value.map((date, i) => (
                <Typography key={i} sx={{ ml: 2 }}>
                  {`- ${new Date(date).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}`}
                </Typography>
              ))}
            </>,
          );
        else if (e.value?.length)
          summaryElements.push(
            <Typography sx={{ ml: 1 }}>
              {`${e.label}: ${new Date(e.value[0]).toLocaleDateString(
                undefined,
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )}`}
            </Typography>,
          );
        return;
      case FormElementType.Radio:
        summaryElements.push(
          <Typography
            sx={{ ml: 1 }}
          >{`${e.label}: ${e.value?.name}`}</Typography>,
        );
        return;
      case FormElementType.MultiSelection:
        summaryElements.push(
          <>
            <Typography sx={{ ml: 1 }}>{`${e.label}:`}</Typography>
            {Array.isArray(e.value) &&
              e.value.length > 0 &&
              e.value.map((item: any, idx: number) => (
                <Typography key={idx} sx={{ ml: 2 }}>
                  {`- ${item.name}`}
                </Typography>
              ))}
          </>,
        );
        return;
      default:
        // eslint-disable-next-line no-case-declarations
        const val = (e as any).value;
        if (typeof val !== "string" && val !== undefined) {
          unableToRender.push(e.label);
        }
        if (val)
          summaryElements.push(
            <Typography sx={{ ml: 1 }}>
              {e.label}: {val.toString()}
            </Typography>,
          );
    }
  });

  if (unableToRender.length > 0) {
    console.warn(
      `Unable to map elements in summary: ${unableToRender.join(", ")}`,
    );
  }

  return (
    <Box
      sx={(t) => ({
        p: 3,
        border: `1px solid ${t.palette.divider}`,
        borderRadius: 2,
        background: t.palette.background.paper,
        maxHeight: "35vh",
        overflow: "scroll",
        margin: "0 auto",
      })}
    >
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
        {summaryElements.map((el, idx) => (
          <Box component="li" key={idx} sx={{ mb: 0 }}>
            {el}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
